<template>
    <BaseModal
        :name="props.name"
        :popover="true"
        :isLoading="!data.ready"
        class=""
    >
        <template v-slot:title v-if="data.ready && data.release">
            <ReleasePreview :item="data.release" :row="true" :invert="true" />
        </template>
        <template v-slot:content v-if="data.ready">
            <div class="items-list" v-if="data.tracks.length">
                <draggable
                    v-model="data.tracks"
                    :item-key="(item) => item.id || item.localId"
                    tag="ul"
                    handle=".sort-button"
                    ghost-class="dragging"
                    @end="onSortEnd"
                    class="items"
                >
                    <template #item="{ element: item }">
                        <li :class="{ opened: player.panes.default?._item?.id === item.id }">
                            <TrackPreview
                                :item="item"
                                :invert="true"
                                :light="true"
                                @play="onTrackPlay"
                            />
                            <Button icon="drag" sprite="navigation"
                                v-if="sortable"
                                class="tertiary submenu size-m sort-button invert" />
                        </li>
                    </template>
                </draggable>
            </div>

            <EmptyLabel
                v-else
                class="empty"
                :invert="true"
                :title="t(`pages.tracks.empty.title`)"
                :description="t(`pages.tracks.empty.description`)"
            />
        </template>
    </BaseModal>
</template>

<script setup>
import { onMounted, reactive, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import draggable from 'vuedraggable'

import BaseModal from '🔗/components/modals/BaseModal.vue';
import Button from '🔗/components/ui/Button.vue';
import IconButton from '🔗/components/ui/IconButton.vue';
import ReleasePreview from '🔗/components/ui/ReleasePreview.vue';
import EmptyLabel from '🔗/components/ui/EmptyLabel.vue';
import TrackPreview from '🔗/components/ui/TrackPreview.vue';
import { delay } from '🔗/utils/utils';

// import api from '🔗/api';
// import stores from '@/stores';

const player = stores.player();
const modals = stores.modals();

const { t } = useI18n();

const props = defineProps({
    name: {
        type: String,
        default: 'release'
    }
});

const data = reactive({
    ready: false,
    release: null,
    tracks: [],
});

const sortable = computed(() => {
    return data.release && api.releases().canEdit({ release: data.release });
});

const onTrackPlay = (track) => {
    player.queue.create(data.tracks, { type: 'release', release: data.release });
}

const onSortEnd = async (evt) => {
    // const payload = {
    //     items: data.tracks.slice(Math.min(evt.newIndex, evt.oldIndex), Math.max(evt.newIndex, evt.oldIndex) + 1),
    //     fromIndex: Math.min(evt.oldIndex, evt.newIndex) + 1
    // }

    data.tracks.forEach((item, id) => {
        if (item.position !== id + 1) {
            if (id === evt.newIndex) {
                api.releases().tracks.setPosition({
                    releaseId: data.id,
                    trackId: item.id,
                    position: id + 1
                });
            }

            item.position = id + 1;
        }
    });
}

const close = () => {
    modals.close(props.name);
}

const load = async () => {
    if (!data.id) {
        close();
        return;
    }

    data.ready = false;

    data.id = modals.getData(props.name);
    if (!data.id) {
        close();
    }

    data.release = await api.releases().get(data.id);

    const payload = await api.releases().tracks.list(data.id) || [];

    data.tracks = payload?.items || [];

    if (!data.release) {
        close();
    }

    await delay(200);

    data.ready = true;
}

onMounted(() => {
    data.id = modals.getData(props.name);
    load();
});
</script>

<style lang="scss" scoped>
    @include studio.default-items-list;

    .items-list {
        overflow: auto;
        width: 100%;
        max-width: none;
        padding-right: 0;
        padding-top: 24px;

        .items {
            padding-top: 0;
            li {
                padding: 8px 24px;
                gap: 8px;

                .sort-button {
                    margin-right: -16px;
                    cursor: grab;
                    fill: var(--white-500);
                }

                &.opened {
                    background: var(--dark-80);
                }
            }
        }
    }
</style>
